import React from 'react';
import { motion } from 'framer-motion';

const PrivacyPolicy = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="pt-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div {...fadeInUp}>
          <h1 className="text-4xl font-bold text-[#0B1320] font-poppins mb-8">
            Privacy Policy
          </h1>
          
          <div className="prose prose-lg max-w-none text-[#4A4A4A] leading-relaxed space-y-8">
            <div>
              <p className="text-sm text-[#9E9E9E] mb-6">
                Last updated: January 2025
              </p>
              
              <p>
                At Sam's Nutri Hub, we are committed to protecting your privacy and ensuring the security 
                of your personal information. This Privacy Policy explains how we collect, use, disclose, 
                and safeguard your information when you visit our website or use our services.
              </p>
            </div>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Information We Collect
              </h2>
              
              <h3 className="text-xl font-semibold text-[#0B1320] mb-3">
                Personal Information
              </h3>
              <p className="mb-4">
                We may collect personal information that you voluntarily provide to us, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Name, email address, phone number</li>
                <li>Health and fitness information</li>
                <li>Dietary preferences and restrictions</li>
                <li>Medical history and conditions (when relevant to our services)</li>
                <li>Payment information (processed securely through third-party providers)</li>
                <li>Communication preferences</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#0B1320] mb-3">
                Automatically Collected Information
              </h3>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>IP address and device information</li>
                <li>Browser type and version</li>
                <li>Pages visited and time spent on our website</li>
                <li>Referring website or source</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                How We Use Your Information
              </h2>
              <p className="mb-4">
                We use the collected information for the following purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Provide personalized nutrition and fitness consultations</li>
                <li>Create customized meal plans and workout programs</li>
                <li>Communicate with you about our services</li>
                <li>Process payments and manage your account</li>
                <li>Send educational content and health tips</li>
                <li>Improve our services and website functionality</li>
                <li>Comply with legal obligations</li>
                <li>Respond to your inquiries and provide customer support</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Information Sharing and Disclosure
              </h2>
              <p className="mb-4">
                We do not sell, trade, or rent your personal information to third parties. 
                We may share your information in the following circumstances:
              </p>
              
              <h3 className="text-xl font-semibold text-[#0B1320] mb-3">
                Service Providers
              </h3>
              <p className="mb-4">
                We may share information with trusted third-party service providers who assist us in:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Payment processing</li>
                <li>Email marketing and communication</li>
                <li>Website hosting and maintenance</li>
                <li>Data analysis and customer support</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#0B1320] mb-3">
                Legal Requirements
              </h3>
              <p className="mb-6">
                We may disclose your information if required by law, court order, or government regulation, 
                or to protect our rights, safety, or the rights and safety of others.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Data Security
              </h2>
              <p className="mb-4">
                We implement appropriate technical and organizational security measures to protect your 
                personal information against unauthorized access, alteration, disclosure, or destruction. 
                These measures include:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>SSL encryption for data transmission</li>
                <li>Secure servers and data storage</li>
                <li>Regular security audits and updates</li>
                <li>Access controls and staff training</li>
                <li>Secure payment processing</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Your Rights and Choices
              </h2>
              <p className="mb-4">
                You have the following rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
                <li><strong>Portability:</strong> Request transfer of your information in a structured format</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time</li>
                <li><strong>Restrict Processing:</strong> Request limitation of how we process your information</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Cookies and Tracking Technologies
              </h2>
              <p className="mb-4">
                We use cookies and similar technologies to enhance your browsing experience. You can:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Control cookie preferences through your browser settings</li>
                <li>Opt-out of certain tracking through browser extensions</li>
                <li>Contact us for information about specific tracking technologies</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Data Retention
              </h2>
              <p className="mb-6">
                We retain your personal information only as long as necessary to provide our services, 
                comply with legal obligations, resolve disputes, and enforce our agreements. 
                When information is no longer needed, we securely delete or anonymize it.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Children's Privacy
              </h2>
              <p className="mb-6">
                Our services are not intended for individuals under 18 years of age. 
                We do not knowingly collect personal information from children under 18. 
                If we become aware that we have collected such information, we will take steps to delete it promptly.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                International Data Transfers
              </h2>
              <p className="mb-6">
                Your information may be transferred to and processed in countries other than your own. 
                We ensure appropriate safeguards are in place to protect your information in accordance 
                with this Privacy Policy and applicable laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Changes to This Privacy Policy
              </h2>
              <p className="mb-6">
                We may update this Privacy Policy from time to time. We will notify you of any material 
                changes by posting the new Privacy Policy on our website and updating the "Last updated" date. 
                We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Contact Information
              </h2>
              <p className="mb-4">
                If you have questions about this Privacy Policy or our privacy practices, please contact us:
              </p>
              <div className="bg-[#F9FFF5] p-6 rounded-lg">
                <p><strong>Sam's Nutri Hub</strong></p>
                <p>Venkatesh Prime, 4th Floor</p>
                <p>Burudgaon Road, Ahmednagar, Maharashtra 414001</p>
                <p>Email: privacy@samsnutrihub.com</p>
                <p>Phone: +91 98765 43210</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Consent
              </h2>
              <p className="mb-6">
                By using our website and services, you consent to the collection, use, and disclosure 
                of your information as described in this Privacy Policy. If you do not agree with this 
                Privacy Policy, please do not use our services.
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;